import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Dropdown, Button, message, Spin, Modal, Form, Input } from 'antd';
import {
  DashboardOutlined,
  DesktopOutlined,
  TeamOutlined,
  VideoCameraOutlined,
  UnorderedListOutlined,
  CalendarOutlined,
  UserOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  FileTextOutlined,
  LineChartOutlined,
  FileProtectOutlined,
  KeyOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '../store/authStore';
import { themeColors } from '../config/theme';
import { userAPI } from '../services/api';

const { Header, Sider, Content, Footer } = Layout;

export default function AppLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [selectedKey, setSelectedKey] = useState(['dashboard']);
  const [loading, setLoading] = useState(true);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordForm] = Form.useForm();
  const user = useAuthStore((state) => state.user);
  const fetchUser = useAuthStore((state) => state.fetchUser);
  const logout = useAuthStore((state) => state.logout);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  useEffect(() => {
    if (isAuthenticated && !user) {
      fetchUser().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, user, fetchUser]);

  useEffect(() => {
    const path = location.pathname.replace('/dashboard/', '').split('/')[0] || 'dashboard';
    setSelectedKey([path]);
  }, [location.pathname]);

  const isSubAdmin = user?.role === 'sub_admin';

  const menuItems = [
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
      onClick: () => navigate('/dashboard'),
    },
    {
      key: 'screens',
      icon: <DesktopOutlined />,
      label: 'Screens',
      onClick: () => navigate('/dashboard/screens'),
    },
    ...(isSubAdmin
      ? []
      : [
          {
            key: 'clients',
            icon: <TeamOutlined />,
            label: 'Clients',
            onClick: () => navigate('/dashboard/clients'),
          },
        ]),
    {
      key: 'videos',
      icon: <VideoCameraOutlined />,
      label: 'Videos',
      onClick: () => navigate('/dashboard/videos'),
    },
    {
      key: 'playlists',
      icon: <UnorderedListOutlined />,
      label: 'Playlists',
      onClick: () => navigate('/dashboard/playlists'),
    },
    {
      key: 'schedules',
      icon: <CalendarOutlined />,
      label: 'Schedules',
      onClick: () => navigate('/dashboard/schedules'),
    },
    {
      key: 'contracts',
      icon: <FileProtectOutlined />,
      label: 'Contracts',
      onClick: () => navigate('/dashboard/contracts'),
    },
    {
      key: 'reports',
      icon: <FileTextOutlined />,
      label: 'Reports',
      onClick: () => navigate('/dashboard/reports'),
    },
    {
      key: 'playback-report',
      icon: <LineChartOutlined />,
      label: 'Playback Report',
      onClick: () => navigate('/dashboard/playback-report'),
    },
  ];

  const userMenuItems = [
    {
      key: 'profile',
      icon: <KeyOutlined />,
      label: 'Change Password',
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      danger: true,
    },
  ];

  const handleUserMenuClick = async (e) => {
    if (e.key === 'logout') {
      await logout();
      message.success('Logged out successfully');
      navigate('/login');
    } else if (e.key === 'profile') {
      setPasswordModalOpen(true);
    }
  };

  const handleChangePassword = async (values) => {
    try {
      setPasswordSaving(true);
      await userAPI.changeOwnPassword(values.currentPassword, values.newPassword);
      message.success('Password changed successfully');
      setPasswordModalOpen(false);
      passwordForm.resetFields();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to change password');
    } finally {
      setPasswordSaving(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: themeColors.bg,
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh', backgroundColor: themeColors.bg }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        style={{
          backgroundColor: themeColors.sidebarBg,
          borderRight: `1px solid ${themeColors.sidebarBorder}`,
        }}
      >
        <div
          style={{
            padding: '20px',
            textAlign: 'center',
            borderBottom: `1px solid ${themeColors.sidebarBorder}`,
            marginBottom: '20px',
          }}
        >
          <img
            src="/cloudnet-logo.png"
            alt="CloudNet"
            style={{
              height: collapsed ? '50px' : '80px',
              marginBottom: '10px',
              objectFit: 'contain',
              transition: 'height 0.3s ease',
            }}
          />
        </div>

        <Menu
          theme="light"
          selectedKeys={selectedKey}
          mode="inline"
          items={menuItems}
          style={{
            backgroundColor: themeColors.sidebarBg,
            borderRight: 'none',
          }}
        />
      </Sider>

      <Layout>
        <Header
          style={{
            backgroundColor: themeColors.sidebarBg,
            borderBottom: `1px solid ${themeColors.sidebarBorder}`,
            padding: '0 20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{ color: themeColors.primary, fontSize: '18px' }}
          />

          <Dropdown menu={{ items: userMenuItems, onClick: handleUserMenuClick }}>
            <Button
              type="text"
              icon={<UserOutlined />}
              style={{
                color: themeColors.primary,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {user?.email}
            </Button>
          </Dropdown>
        </Header>

        <Content
          style={{
            padding: '24px',
            backgroundColor: themeColors.bg,
            overflow: 'auto',
          }}
        >
          {children}
        </Content>

        <Footer
          style={{
            textAlign: 'center',
            backgroundColor: themeColors.bg,
            color: themeColors.textSecondary,
            padding: '12px 24px',
          }}
        >
          Designed & Developed by CloudNet Softwares | +66-948712350
        </Footer>
      </Layout>

      <Modal
        title="Change Password"
        open={passwordModalOpen}
        onOk={() => passwordForm.submit()}
        onCancel={() => {
          setPasswordModalOpen(false);
          passwordForm.resetFields();
        }}
        confirmLoading={passwordSaving}
      >
        <Form form={passwordForm} layout="vertical" onFinish={handleChangePassword} style={{ marginTop: '20px' }}>
          <Form.Item
            name="currentPassword"
            label="Current Password"
            rules={[{ required: true, message: 'Current password is required' }]}
          >
            <Input.Password placeholder="Current password" />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label="New Password"
            rules={[{ required: true, message: 'New password is required' }, { min: 6, message: 'Minimum 6 characters' }]}
          >
            <Input.Password placeholder="New password" />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
}
